import React, { useEffect, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import {
    fetchTicketsSettingsForQueue,
    setTicketsSettingsForQueue,
} from 'flow/actions/tickets-action';
import PbxTicketsSettingsVisibilityRow from 'flow/components/pbx/PbxTicketsSettingsVisibilityRow';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import Divider from 'flow/components/ui-components/Divider';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectQueueEditable } from 'flow/selectors/queue-selectors';
import {
    selectCloseOnHangupSetting,
    selectCloseOnMissedCallSetting,
    selectTicketCreationSetting,
} from 'flow/selectors/QueueTicketsSettingsSelectors';
import { selectQueueIsEligibleForTicketsConfiguration } from 'flow/selectors/TicketsSelectors';
import { QueueKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import {
    TICKETS_SETTINGS_KEYS,
    getQueueCreateTicketSettingsOptionI18Key,
} from 'flow/utils/ticket-utils';

type SettingKey = 'closeOnMissedCall' | 'closeOnHangUp';

const QueueTicketsSettings = () => {
    const { i18next } = useAppContext();
    const { entityKey: queueEntityKey } = useParams<{ entityKey: QueueKey }>();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const queueIsEligibleForTicketsConfiguration = useAppSelector((state) =>
        selectQueueIsEligibleForTicketsConfiguration(state, queueEntityKey),
    );

    const userCanEditQueue = useAppSelector((state) =>
        selectQueueEditable(state, queueEntityKey),
    );

    const userCanActivateTicketsOnQueue =
        queueIsEligibleForTicketsConfiguration && userCanEditQueue;

    const ticketCreationSetting =
        useAppSelector((state) =>
            selectTicketCreationSetting(state, queueEntityKey),
        ) || TICKETS_SETTINGS_KEYS.AUTO_CREATION.NEVER;

    const closeOnHangupSetting = useAppSelector((state) =>
        selectCloseOnHangupSetting(state, queueEntityKey),
    );
    const closeOnMissedCallSetting = useAppSelector((state) =>
        selectCloseOnMissedCallSetting(state, queueEntityKey),
    );

    const [isPerformingApiRequest, setIsPerformingApiRequest] = useState(false);
    const [isFetchingSettings, setIsFetchingSettings] = useState(false);

    useEffect(() => {
        setIsPerformingApiRequest(true);
        setIsFetchingSettings(true);
        dispatch(fetchTicketsSettingsForQueue(queueEntityKey)).finally(() => {
            setIsPerformingApiRequest(false);
            setIsFetchingSettings(false);
        });
    }, [dispatch, queueEntityKey]);

    const onSettingToggled = (key: SettingKey, value: boolean) => {
        setIsPerformingApiRequest(true);
        dispatch(fetchTicketsSettingsForQueue(queueEntityKey)).finally(() => {
            dispatch(
                setTicketsSettingsForQueue(queueEntityKey, key, value),
            ).finally(() => {
                setIsPerformingApiRequest(false);
            });
        });
    };

    return (
        <Page
            header={i18next.t('route.tickets')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['pbx.queues.entityKey.settings'], {
                        entityKey: queueEntityKey,
                    }),
                )
            }
        >
            <>
                <Divider />
                <RouteLink
                    to={generatePath(
                        routes['pbx.queues.entityKey.settings.tickets.create'],
                        {
                            entityKey: queueEntityKey,
                        },
                    )}
                    description={
                        isFetchingSettings
                            ? undefined
                            : i18next.t(
                                  getQueueCreateTicketSettingsOptionI18Key(
                                      ticketCreationSetting,
                                  ),
                              )
                    }
                    disabled={!userCanActivateTicketsOnQueue}
                >
                    {i18next.t('ticket.auto.create.tickets.on.incoming.calls')}
                </RouteLink>
                <SettingRow
                    cta={
                        <Toggle
                            value={closeOnHangupSetting}
                            onChange={(value) =>
                                onSettingToggled('closeOnHangUp', value)
                            }
                            disabled={
                                !userCanActivateTicketsOnQueue ||
                                ticketCreationSetting ===
                                    TICKETS_SETTINGS_KEYS.AUTO_CREATION.NEVER ||
                                ticketCreationSetting ===
                                    TICKETS_SETTINGS_KEYS.AUTO_CREATION
                                        .MISSED_CALLS ||
                                isPerformingApiRequest
                            }
                            text={i18next.t(
                                'ticket.settings.queue.close.on.hangup',
                            )}
                        />
                    }
                    className={
                        !userCanActivateTicketsOnQueue ? 'text-gray-400' : ''
                    }
                >
                    {i18next.t('ticket.settings.queue.close.on.hangup')}
                </SettingRow>
                <SettingRow
                    cta={
                        <Toggle
                            value={closeOnMissedCallSetting}
                            onChange={(value) =>
                                onSettingToggled('closeOnMissedCall', value)
                            }
                            disabled={
                                !userCanActivateTicketsOnQueue ||
                                ticketCreationSetting ===
                                    TICKETS_SETTINGS_KEYS.AUTO_CREATION.NEVER ||
                                isPerformingApiRequest
                            }
                            text={i18next.t(
                                'ticket.settings.queue.close.missedcall',
                            )}
                        />
                    }
                    className={
                        !userCanActivateTicketsOnQueue ? 'text-gray-400' : ''
                    }
                >
                    {i18next.t('ticket.settings.queue.close.missedcall')}
                </SettingRow>
                <PbxTicketsSettingsVisibilityRow
                    queueEntityKey={queueEntityKey}
                    disabled={!userCanActivateTicketsOnQueue}
                />
                <Divider />
                {!queueIsEligibleForTicketsConfiguration ? (
                    <p className="rounded bg-gray-200 p-4 text-sm text-gray-800">
                        {i18next.t('Queue.Settings.Tickets.Eligibility', {
                            serviceName: i18next.t('route.tickets'),
                        })}
                    </p>
                ) : null}
            </>
        </Page>
    );
};

export default QueueTicketsSettings;
